// @flow

import React from 'react';
import { I18n } from 'react-redux-i18n';
import MallMarketingTile from './items/MallMarketingTile';
import { getCurrentShoppingCenter } from '../../../utils/urlUtils';
import PenetrationRateTableau from './items/PenetrationRateTableau';
import type { ShoppingCenter } from '../../../types/shoppingCenterType';
import Loader from '../../common/Loader';
import DominantCustomerProfile from './items/DominantCustomerProfile';
import MonthlyLoyaltySurvey from './items/MonthlyLoyaltySurvey';
import KeyFigures from './items/KeyFigures';
import WeightedSpendPotential from './items/WeightedSpendPotential';
import type { MediaGallery } from '../../../types/keepeekType';
import MarketingCatchmentArea from './items/MarketingCatchmentArea';
import CustomerProfile from './items/CustomerProfile';
import KeyFigure from '../../office/component/KeyFigure';
import marketingApi from '../../../network/api/marketingApi';
import type { CameoGroupsBean } from '../../../types/apolloType';
import type { CentreTradeArea, DominantCustomerProfileType } from '../../../types/marketingType';
import { notifyApolloError } from '../../../network/notification';

type Props = {
  dispatch: () => void,
  shoppingCenters: ShoppingCenter[],
  shoppingCenterMedia: MediaGallery[],
  isKeyFiguresLoading: boolean,
  keyFigures: KeyFigure,
}

type State = {
  customerProfile: ?CameoGroupsBean,
  isCustomerProfileLoading: boolean,
  dominantCustomerProfiles: DominantCustomerProfileType[],
  isDominantCustomerLoading: boolean,
  isCentreTradeAreasLoading: boolean,
  centreTradeArea: CentreTradeArea[],
}

class MallMarketing extends React.Component<Props, State> {
  state: State = {
    isCustomerProfileLoading: true,
    customerProfile: null,
    dominantCustomerProfiles: [],
    isDominantCustomerLoading: true,
    isCentreTradeAreasLoading: true,
    centreTradeArea: [],
  }

  componentDidMount() {
    const shoppingCenter = getCurrentShoppingCenter(window.location.href, this.props.shoppingCenters);
    this.updateCameoData(shoppingCenter);
    this.fetchCentreTradeAreas(shoppingCenter);
  }

  shouldComponentUpdate(nextProps) {
    if (this.props.shoppingCenters !== nextProps.shoppingCenters) {
      const shoppingCenter = getCurrentShoppingCenter(window.location.href, nextProps.shoppingCenters);
      this.updateCameoData(shoppingCenter);
      this.fetchCentreTradeAreas(shoppingCenter);
    }
    return true;
  }

  fetchCentreTradeAreas(shoppingCenter) {
    if (shoppingCenter != null) {
      marketingApi.fetchCentreTradeAreas(shoppingCenter.id)
        .then(json => json.json())
        .then((centreTradeArea: CentreTradeArea[]) => {
          this.setState({
            centreTradeArea,
          });
        })
        .catch(e => this.props.dispatch(notifyApolloError(e)))
        .finally(() => this.setState({ isCentreTradeAreasLoading: false }));
    }
  }

  updateCameoData(shoppingCenter) {
    if (shoppingCenter != null) {
      marketingApi.getDominantCustomerProfile(shoppingCenter.id)
        .then(response => response.json())
        .then(dominantCustomerProfiles => this.setState({ dominantCustomerProfiles }))
        .catch(e => this.props.dispatch(notifyApolloError(e)))
        .finally(() => this.setState({ isDominantCustomerLoading: false }));

      marketingApi.fetchMarketingCameoGroups(shoppingCenter.id)
        .then(response => response.json())
        .then(response => this.setState({ customerProfile: response }))
        .catch(e => this.props.dispatch(notifyApolloError(e)))
        .finally(() => this.setState({ isCustomerProfileLoading: false }));
    }
  }

  tooltipContentByArea = (area: string, dataYear?: number) => {
    if (area === 'key-figures') {
      return (
        <div className="marketing-tooltip">
          <span>
            {I18n.t('MKT_PAGE.key_figures.tooltip.title')}
          </span>
        </div>
      );
    }
    if (area === 'trade') {
      return (
        <div className="marketing-tooltip">
          <span>
            {I18n.t('MKT_PAGE.center_trade_area.tooltip')}
          </span>
        </div>
      );
    }
    if (area === 'weighted-spend-potential') {
      return (
        <div className="marketing-tooltip">
          <span>
            {I18n.t('MKT_PAGE.weighted_spend_potential.tooltip')}
          </span>
        </div>
      );
    }
    if (area === 'penetration') {
      return (
        <div className="marketing-tooltip">
          <span>
            {I18n.t('MKT_PAGE.penetration_rate.tooltip')}
          </span>
        </div>
      );
    }
    if (area === 'dominant') {
      return (
        <div className="marketing-tooltip">
          <span>
            {
              dataYear
                ? I18n.t('MKT_PAGE.dominantCustomer.tooltip', { year: dataYear })
                : I18n.t('MKT_PAGE.dominantCustomer.tooltipNoData')
            }
          </span>
        </div>
      );
    }
    if (area === 'customer') {
      return (
        <div className="marketing-tooltip">
          <span>
            {
              dataYear
                ? I18n.t('MKT_PAGE.customer_profile.tooltip', { year: dataYear })
                : I18n.t('MKT_PAGE.customer_profile.tooltipNoData')
            }
          </span>
        </div>
      );
    }
    if (area === 'loyalty-survey') {
      return (
        <div className="marketing-tooltip">
          <span>
            {I18n.t('MKT_PAGE.monthly_survey.tooltip')}
          </span>
        </div>
      );
    }
    return 'N/A';
  };

  render() {
    if (this.props.shoppingCenters === null || this.props.shoppingCenters.length === 0) {
      return <Loader />;
    }
    const shoppingCenter = getCurrentShoppingCenter(window.location.href, this.props.shoppingCenters);
    const { shoppingCenterMedia } = this.props;

    return (
      <div className="mall-marketing scrollable-content">
        <div className="banner">
          <div className="banner-icon-wrapper">
            <i className="fa fa-exclamation-triangle" />
          </div>
          <div className="banner-message">{I18n.t('MKT_PAGE.banner_message')}</div>
        </div>
        <div id="key-figures">
          <MallMarketingTile
            title={I18n.t('MKT_PAGE.key_figures.title')}
            hideTooltip
          >
            <KeyFigures
              isKeyFiguresLoading={this.props.isKeyFiguresLoading}
              keyFigures={this.props.keyFigures}
              centreTradeArea={this.state.centreTradeArea}
            />
          </MallMarketingTile>
        </div>

        <div id="trade">
          <MallMarketingTile
            title={I18n.t('MKT_PAGE.center_trade_area.title')}
            tooltipContent={this.tooltipContentByArea('trade')}
          >
            <MarketingCatchmentArea
              isLoading={this.state.isCentreTradeAreasLoading}
              centreTradeArea={this.state.centreTradeArea}
              shoppingCenterMedia={shoppingCenterMedia}
            />
          </MallMarketingTile>
        </div>


        <div id="weighted-spend-potential">
          <MallMarketingTile
            title={I18n.t('MKT_PAGE.weighted_spend_potential.title')}
            tooltipContent={this.tooltipContentByArea('weighted-spend-potential')}
          >
            <WeightedSpendPotential shoppingCenter={shoppingCenter} />
          </MallMarketingTile>
        </div>

        <div id="penetration">
          <MallMarketingTile
            title={I18n.t('MKT_PAGE.penetration_rate.title')}
            tooltipContent={this.tooltipContentByArea('penetration')}
          >
            <PenetrationRateTableau
              shoppingCenter={shoppingCenter}
            />
          </MallMarketingTile>
        </div>

        <div id="dominant">
          <MallMarketingTile
            title={I18n.t('MKT_PAGE.dominantCustomer.title')}
            tooltipContent={this.tooltipContentByArea('dominant', this.state.dominantCustomerProfiles.length !== 0 && this.state.dominantCustomerProfiles[0].year)}
          >
            <div className="dominant-customer-container">
              <DominantCustomerProfile
                dominantCustomerProfiles={this.state.dominantCustomerProfiles}
                isLoading={this.state.isDominantCustomerLoading}
              />
            </div>
          </MallMarketingTile>
        </div>

        <div id="customer">
          <MallMarketingTile
            title={I18n.t('MKT_PAGE.customer_profile.title')}
            tooltipContent={this.tooltipContentByArea('customer', this.state.customerProfile && this.state.customerProfile.year)}
          >
            <CustomerProfile
              customerProfile={this.state.customerProfile}
              isLoading={this.state.isCustomerProfileLoading}
            />
          </MallMarketingTile>
        </div>

        <div id="loyalty-survey">
          <MallMarketingTile
            title={I18n.t('MKT_PAGE.monthly_survey.title')}
            tooltipContent={this.tooltipContentByArea('loyalty-survey')}
          >
            <MonthlyLoyaltySurvey
              dispatch={this.props.dispatch}
              shoppingCenter={shoppingCenter}
            />
          </MallMarketingTile>
        </div>
      </div>
    );
  }
}

export default MallMarketing;
